import { useEffect } from 'react';

interface Module {
  id: number;
  title: string;
  description: string;
  image: string;
  category: string;
}

interface ModuleModalProps {
  module: Module | null;
  onClose: () => void;
}

export default function ModuleModal({ module, onClose }: ModuleModalProps) {
  useEffect(() => {
    if (module) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [module]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  if (!module) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-foreground-950/70 backdrop-blur-sm transition-opacity" />

      {/* Content */}
      <div
        className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-background-50 rounded-2xl shadow-2xl animate-in fade-in zoom-in-95 duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-background-100 hover:bg-background-200 flex items-center justify-center text-foreground-600 hover:text-foreground-900 transition-colors cursor-pointer"
          aria-label="Cerrar"
        >
          <i className="ri-close-line text-xl" />
        </button>

        {/* Image */}
        <div className="relative">
          <div className="laptop-mockup m-6 mb-0">
            <div className="laptop-screen aspect-[16/10]">
              <img
                src={module.image}
                alt={module.title}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>

        {/* Text content */}
        <div className="p-6 md:p-8">
          <div className="flex items-center gap-2 mb-3">
            <span className="px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-semibold">
              {module.category}
            </span>
          </div>

          <h3
            id="modal-title"
            className="font-heading text-xl md:text-2xl font-bold text-primary-900"
          >
            {module.title}
          </h3>

          <p className="mt-3 text-base text-foreground-600 leading-relaxed">
            {module.description}
          </p>

          <div className="mt-6 flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-5 py-2.5 rounded-lg bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold transition-colors cursor-pointer"
            >
              Cerrar
            </button>
            <a
              href="#contacto"
              onClick={(e) => {
                e.preventDefault();
                onClose();
                const target = document.querySelector('#contacto');
                if (target) target.scrollIntoView({ behavior: 'smooth' });
              }}
              className="px-5 py-2.5 rounded-lg border border-primary-300 text-primary-700 hover:bg-primary-50 text-sm font-medium transition-colors"
            >
              Solicitar demo
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}